<script setup lang="ts">
/**
 * The address field of a link, with Apply and Remove — in the toolbar's link
 * popover, or under „⋯“ when the insert tools are not in the row.
 */
import { onBeforeUnmount, ref } from 'vue';
import { NxButton } from '../Button';

defineProps<{
  urlLabel: string;
  applyLabel: string;
  removeLabel: string;
}>();

const emit = defineEmits<{ apply: []; remove: []; cancel: [] }>();
const href = defineModel<string>({ required: true });
const input = ref<HTMLInputElement | null>(null);

// An IME's last Enter ends the composition; for a moment after it, Enter is
// not an "apply the link".
let justComposed = false;
let justComposedTimer: ReturnType<typeof setTimeout> | undefined;
function onCompositionEnd(): void {
  justComposed = true;
  clearTimeout(justComposedTimer);
  justComposedTimer = setTimeout(() => {
    justComposed = false;
  }, 80);
}
onBeforeUnmount(() => clearTimeout(justComposedTimer));

function onKeydown(event: KeyboardEvent): void {
  if (event.isComposing || event.keyCode === 229 || justComposed) return;
  if (event.key === 'Enter') {
    event.preventDefault();
    emit('apply');
  } else if (event.key === 'Escape') {
    event.preventDefault();
    emit('cancel');
  }
}

defineExpose({ focus: () => input.value?.focus() });
</script>

<template>
  <div class="nx-rich-text-link">
    <input
      ref="input"
      v-model="href"
      class="nx-rich-text-link__input"
      type="url"
      placeholder="https://"
      :aria-label="urlLabel"
      @compositionend="onCompositionEnd"
      @keydown="onKeydown"
    />
    <NxButton height="xs" @click="emit('apply')">{{ applyLabel }}</NxButton>
    <NxButton height="xs" variant="ghost" @click="emit('remove')">{{ removeLabel }}</NxButton>
  </div>
</template>

<style scoped>
.nx-rich-text-link {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
}

.nx-rich-text-link__input {
  width: calc(var(--nxt-size-unit) * 15);
  height: var(--nxt-control-size-sm);
  padding: 0 var(--nxt-space-xs);
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-sm);
  outline: none;
  background: var(--nxt-surface-bg);
  color: var(--nxt-fg);
  font-size: var(--nxt-font-size-xs);
}

.nx-rich-text-link__input:focus {
  border-color: var(--nxt-brand-bg);
  box-shadow: 0 0 0 var(--nxt-space-2xs) var(--nxt-focus-ring-soft);
}
</style>
